import React, { useState, useEffect, useRef } from 'react';
import { FaUsers, FaHandshake, FaBriefcase, FaListUl, FaCogs, FaPlus, FaEdit, FaTrash, FaSave, FaEye, FaTimes } from 'react-icons/fa';
import { cmsAPI, getImageUrl } from '../../utils/api';
import '../../styles/pages/admin/AdminCommon.scss';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:8000';

function AdminCollaborations() {
  const [activeTab, setActiveTab] = useState('sections');
  const [loading, setLoading] = useState(true);

  // Lists Data
  const [companyPartners, setCompanyPartners] = useState([]);
  const [csrPartners, setCsrPartners] = useState([]);
  const [collabProjects, setCollabProjects] = useState([]);
  const [collabFeatures, setCollabFeatures] = useState([]);

  // Sections (HomeSections keys) Data
  const [sections, setSections] = useState({});
  const [activeSectionKey, setActiveSectionKey] = useState('collaborations_hero');
  const [sectionForm, setSectionForm] = useState({
    title: '',
    subtitle: '',
    description: '',
    image: null
  });
  const [sectionImagePreview, setSectionImagePreview] = useState(null);
  const sectionFileRef = useRef(null);

  // Modals / Form controls
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'company', 'csr', 'project', 'feature'
  const [editingId, setEditingId] = useState(null);

  // Generic forms
  const [companyForm, setCompanyForm] = useState({ name: '', description: '', website: '', image: null });
  const [csrForm, setCsrForm] = useState({ name: '', image: null });
  const [featureForm, setFeatureForm] = useState({ icon: '🚀', title: '', description: '' });
  const [projectForm, setProjectForm] = useState({
    title: '',
    description: '',
    details: '',
    reverse: false,
    image: null,
    highlights: [{ number: '', label: '' }]
  });

  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Fetch HomeSections for text configs
      const homeRes = await cmsAPI.getHome();
      if (homeRes.data.success) {
        const sectMap = {};
        homeRes.data.data.forEach(sec => {
          if (sec.section_key.startsWith('collaborations_')) {
            sectMap[sec.section_key] = sec;
          }
        });
        setSections(sectMap);
        
        // Initialize current section form
        const currSec = sectMap[activeSectionKey];
        if (currSec) {
          setSectionForm({
            title: currSec.title || '',
            subtitle: currSec.subtitle || '',
            description: currSec.description || '',
            image: null
          });
          setSectionImagePreview(currSec.image ? getImageUrl(currSec.image) : null);
        }
      }

      // 2. Fetch Lists
      const companyRes = await cmsAPI.getCompanyPartners();
      if (companyRes.data.success) setCompanyPartners(companyRes.data.data);

      const csrRes = await cmsAPI.getCsrPartners();
      if (csrRes.data.success) setCsrPartners(csrRes.data.data);

      const projectsRes = await cmsAPI.getCollaborationProjects();
      if (projectsRes.data.success) setCollabProjects(projectsRes.data.data);

      const featuresRes = await cmsAPI.getCollaborationFeatures();
      if (featuresRes.data.success) setCollabFeatures(featuresRes.data.data);

    } catch (error) {
      console.error('Error fetching collaborations data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Change section configuration tab
  useEffect(() => {
    const currSec = sections[activeSectionKey];
    if (currSec) {
      setSectionForm({
        title: currSec.title || '',
        subtitle: currSec.subtitle || '',
        description: currSec.description || '',
        image: null
      });
      setSectionImagePreview(currSec.image ? getImageUrl(currSec.image) : null);
      if (sectionFileRef.current) sectionFileRef.current.value = '';
    } else {
      setSectionForm({ title: '', subtitle: '', description: '', image: null });
      setSectionImagePreview(null);
    }
  }, [activeSectionKey, sections]);

  // Section forms update handler
  const handleSectionSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('title', sectionForm.title);
    data.append('subtitle', sectionForm.subtitle);
    data.append('description', sectionForm.description);
    if (sectionForm.image) {
      data.append('image', sectionForm.image);
    }

    try {
      const res = await cmsAPI.updateHome(activeSectionKey, data);
      if (res.data.success) {
        alert('Section content updated successfully!');
        // Refresh sections map
        setSections(prev => ({
          ...prev,
          [activeSectionKey]: res.data.data
        }));
      } else {
        alert('Failed to update section content');
      }
    } catch (err) {
      console.error(err);
      alert('Error updating section content');
    }
  };

  // Generic file pick helpers
  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (type === 'section') {
      setSectionForm(prev => ({ ...prev, image: file }));
      setSectionImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview(URL.createObjectURL(file));
      if (type === 'company') setCompanyForm(prev => ({ ...prev, image: file }));
      if (type === 'csr') setCsrForm(prev => ({ ...prev, image: file }));
      if (type === 'project') setProjectForm(prev => ({ ...prev, image: file }));
    }
  };

  // Reset form states
  const resetFormState = () => {
    setEditingId(null);
    setShowModal(false);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    setCompanyForm({ name: '', description: '', website: '', image: null });
    setCsrForm({ name: '', image: null });
    setFeatureForm({ icon: '🚀', title: '', description: '' });
    setProjectForm({
      title: '',
      description: '',
      details: '',
      reverse: false,
      image: null,
      highlights: [{ number: '', label: '' }]
    });
  };

  const handleEditClick = (type, item) => {
    setModalType(type);
    setEditingId(item.id);

    if (type === 'company') {
      setCompanyForm({
        name: item.name || '',
        description: item.description || '',
        website: item.website || '',
        image: null
      });
      setImagePreview(item.logo ? getImageUrl(item.logo) : null);
    } else if (type === 'csr') {
      setCsrForm({
        name: item.name || '',
        image: null
      });
      setImagePreview(item.logo ? getImageUrl(item.logo) : null);
    } else if (type === 'feature') {
      setFeatureForm({
        icon: item.icon || '🚀',
        title: item.title || '',
        description: item.description || ''
      });
    } else if (type === 'project') {
      let parsedHighlights = [{ number: '', label: '' }];
      if (item.highlights) {
        try {
          parsedHighlights = typeof item.highlights === 'string' ? JSON.parse(item.highlights) : item.highlights;
        } catch (e) {
          console.error('Error parsing highlights:', e);
        }
      }
      setProjectForm({
        title: item.title || '',
        description: item.description || '',
        details: item.details || '',
        reverse: !!item.reverse,
        image: null,
        highlights: parsedHighlights
      });
      setImagePreview(item.image ? getImageUrl(item.image) : null);
    }

    setShowModal(true);
  };

  // Delete calls
  const handleDeleteClick = async (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;

    try {
      let res;
      if (type === 'company') {
        res = await cmsAPI.deleteCompanyPartner(id);
        if (res.data.success) setCompanyPartners(prev => prev.filter(x => x.id !== id));
      } else if (type === 'csr') {
        res = await cmsAPI.deleteCsrPartner(id);
        if (res.data.success) setCsrPartners(prev => prev.filter(x => x.id !== id));
      } else if (type === 'project') {
        res = await cmsAPI.deleteCollaborationProject(id);
        if (res.data.success) setCollabProjects(prev => prev.filter(x => x.id !== id));
      } else if (type === 'feature') {
        res = await cmsAPI.deleteCollaborationFeature(id);
        if (res.data.success) setCollabFeatures(prev => prev.filter(x => x.id !== id));
      }
      alert('Deleted successfully!');
    } catch (err) {
      console.error(err);
      alert('Failed to delete item');
    }
  };

  // List Submit calls (CRUD - Add / Edit)
  const handleModalSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();

    try {
      if (modalType === 'company') {
        data.append('name', companyForm.name);
        data.append('description', companyForm.description);
        data.append('website', companyForm.website);
        if (companyForm.image) data.append('image', companyForm.image);

        let res;
        if (editingId) {
          res = await cmsAPI.updateCompanyPartner(editingId, data);
          if (res.data.success) {
            setCompanyPartners(prev => prev.map(x => x.id === editingId ? res.data.data : x));
          }
        } else {
          res = await cmsAPI.createCompanyPartner(data);
          if (res.data.success) {
            setCompanyPartners(prev => [...prev, res.data.data]);
          }
        }
      } else if (modalType === 'csr') {
        data.append('name', csrForm.name);
        if (csrForm.image) data.append('image', csrForm.image);
        else if (!editingId) {
          alert('CSR Partner Logo is required for new items');
          return;
        }

        let res;
        if (editingId) {
          res = await cmsAPI.updateCsrPartner(editingId, data);
          if (res.data.success) {
            setCsrPartners(prev => prev.map(x => x.id === editingId ? res.data.data : x));
          }
        } else {
          res = await cmsAPI.createCsrPartner(data);
          if (res.data.success) {
            setCsrPartners(prev => [...prev, res.data.data]);
          }
        }
      } else if (modalType === 'feature') {
        data.append('icon', featureForm.icon);
        data.append('title', featureForm.title);
        data.append('description', featureForm.description);

        let res;
        if (editingId) {
          res = await cmsAPI.updateCollaborationFeature(editingId, data);
          if (res.data.success) {
            setCollabFeatures(prev => prev.map(x => x.id === editingId ? res.data.data : x));
          }
        } else {
          res = await cmsAPI.createCollaborationFeature(data);
          if (res.data.success) {
            setCollabFeatures(prev => [...prev, res.data.data]);
          }
        }
      } else if (modalType === 'project') {
        data.append('title', projectForm.title);
        data.append('description', projectForm.description);
        data.append('details', projectForm.details);
        data.append('reverse', projectForm.reverse ? 'true' : 'false');
        data.append('highlights', JSON.stringify(projectForm.highlights));
        if (projectForm.image) data.append('image', projectForm.image);

        let res;
        if (editingId) {
          res = await cmsAPI.updateCollaborationProject(editingId, data);
          if (res.data.success) {
            setCollabProjects(prev => prev.map(x => x.id === editingId ? res.data.data : x));
          }
        } else {
          res = await cmsAPI.createCollaborationProject(data);
          if (res.data.success) {
            setCollabProjects(prev => [...prev, res.data.data]);
          }
        }
      }

      alert('Item saved successfully!');
      resetFormState();
    } catch (err) {
      console.error(err);
      alert('Error saving item');
    }
  };

  // Highlights input handlers for Projects Form
  const handleHighlightChange = (index, field, value) => {
    const updatedHighlights = [...projectForm.highlights];
    updatedHighlights[index][field] = value;
    setProjectForm(prev => ({ ...prev, highlights: updatedHighlights }));
  };

  const addHighlightField = () => {
    setProjectForm(prev => ({
      ...prev,
      highlights: [...prev.highlights, { number: '', label: '' }]
    }));
  };

  const removeHighlightField = (index) => {
    if (projectForm.highlights.length <= 1) return;
    setProjectForm(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, idx) => idx !== index)
    }));
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>CMS - Collaborations Section</h1>
        <p>Manage partners, CSR projects, copy text, and why partner features</p>
      </div>

      {/* Tabs Menu */}
      <div className="tabs-container" style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #ddd', paddingBottom: '10px' }}>
        <button className={`btn ${activeTab === 'sections' ? 'btn-primary' : 'btn-outline-dark'}`} onClick={() => setActiveTab('sections')}>
          <FaCogs style={{ marginRight: '5px' }} /> Copy & Sections
        </button>
        <button className={`btn ${activeTab === 'company' ? 'btn-primary' : 'btn-outline-dark'}`} onClick={() => setActiveTab('company')}>
          <FaHandshake style={{ marginRight: '5px' }} /> Company Partners ({companyPartners.length})
        </button>
        <button className={`btn ${activeTab === 'csr' ? 'btn-primary' : 'btn-outline-dark'}`} onClick={() => setActiveTab('csr')}>
          <FaUsers style={{ marginRight: '5px' }} /> CSR Partners ({csrPartners.length})
        </button>
        <button className={`btn ${activeTab === 'project' ? 'btn-primary' : 'btn-outline-dark'}`} onClick={() => setActiveTab('project')}>
          <FaBriefcase style={{ marginRight: '5px' }} /> CSR Projects ({collabProjects.length})
        </button>
        <button className={`btn ${activeTab === 'feature' ? 'btn-primary' : 'btn-outline-dark'}`} onClick={() => setActiveTab('feature')}>
          <FaListUl style={{ marginRight: '5px' }} /> Features List ({collabFeatures.length})
        </button>
      </div>

      {loading ? (
        <div className="content-card" style={{ padding: '40px', textAlign: 'center' }}>
          <p>Loading collaborations data...</p>
        </div>
      ) : (
        <div className="content-card">
          {/* TAB 1: Sections copy editing */}
          {activeTab === 'sections' && (
            <div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <button className={`btn btn-sm ${activeSectionKey === 'collaborations_hero' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setActiveSectionKey('collaborations_hero')}>Hero Section</button>
                <button className={`btn btn-sm ${activeSectionKey === 'collaborations_csr_intro' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setActiveSectionKey('collaborations_csr_intro')}>CSR Intro</button>
                <button className={`btn btn-sm ${activeSectionKey === 'collaborations_why_partner_intro' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setActiveSectionKey('collaborations_why_partner_intro')}>Why Collaborate Intro</button>
                <button className={`btn btn-sm ${activeSectionKey === 'collaborations_cta' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setActiveSectionKey('collaborations_cta')}>CTA Section</button>
              </div>

              <form onSubmit={handleSectionSubmit} style={{ maxWidth: '600px', margin: '0 auto' }}>
                <h3>Editing: {activeSectionKey.replace('collaborations_', '').toUpperCase().replace('_', ' ')}</h3>
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label>Title</label>
                  <input type="text" className="form-control" value={sectionForm.title} onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })} required />
                </div>
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label>Subtitle / Tagline</label>
                  <input type="text" className="form-control" value={sectionForm.subtitle} onChange={(e) => setSectionForm({ ...sectionForm, subtitle: e.target.value })} />
                </div>
                {activeSectionKey !== 'collaborations_hero' && (
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Description Content</label>
                    <textarea className="form-control" rows="5" value={sectionForm.description} onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })}></textarea>
                  </div>
                )}
                {activeSectionKey !== 'collaborations_why_partner_intro' && (
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label>Background/Intro Image</label>
                    <input type="file" ref={sectionFileRef} className="form-control" accept="image/*" onChange={(e) => handleFileChange(e, 'section')} />
                    {sectionImagePreview && (
                      <div className="image-preview" style={{ marginTop: '10px' }}>
                        <img src={sectionImagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', border: '1px solid #ddd' }} />
                      </div>
                    )}
                  </div>
                )}
                <button type="submit" className="btn btn-primary"><FaSave style={{ marginRight: '5px' }} /> Save Section Changes</button>
              </form>
            </div>
          )}

          {/* TAB 2: Company Partners */}
          {activeTab === 'company' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Company Partners</h2>
                <button className="btn btn-primary" onClick={() => { setModalType('company'); setShowModal(true); }}>
                  <FaPlus /> Add Partner
                </button>
              </div>

              {companyPartners.length === 0 ? (
                <p>No Company Partners added yet.</p>
              ) : (
                <div className="items-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                  {companyPartners.map(item => (
                    <div key={item.id} className="item-card" style={{ border: '1px solid #eee', padding: '15px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #f9f9f9', paddingBottom: '10px' }}>
                        <img src={getImageUrl(item.logo)} alt={item.name} style={{ maxHeight: '100%', maxWidth: '150px', objectFit: 'contain' }} />
                      </div>
                      <div style={{ flexGrow: 1 }}>
                        <h3 style={{ margin: '5px 0' }}>{item.name}</h3>
                        <p style={{ fontSize: '0.85rem', color: '#666' }}>{item.description || 'No description available'}</p>
                        {item.website && <a href={item.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85rem', color: '#00526A' }}>Website link</a>}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <button className="btn btn-sm btn-outline-dark" style={{ flex: 1 }} onClick={() => handleEditClick('company', item)}><FaEdit /> Edit</button>
                        <button className="btn btn-sm btn-outline-danger" style={{ flex: 1 }} onClick={() => handleDeleteClick('company', item.id)}><FaTrash /> Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: CSR Partners */}
          {activeTab === 'csr' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>CSR Partner Logos</h2>
                <button className="btn btn-primary" onClick={() => { setModalType('csr'); setShowModal(true); }}>
                  <FaPlus /> Add CSR Logo
                </button>
              </div>

              {csrPartners.length === 0 ? (
                <p>No CSR partners added yet.</p>
              ) : (
                <div className="items-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '20px' }}>
                  {csrPartners.map(item => (
                    <div key={item.id} className="item-card" style={{ border: '1px solid #eee', padding: '15px', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', textAlign: 'center' }}>
                      <div style={{ height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                        <img src={getImageUrl(item.logo)} alt={item.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                      </div>
                      <h4 style={{ margin: '5px 0', fontSize: '0.95rem' }}>{item.name}</h4>
                      <div style={{ display: 'flex', gap: '5px', width: '100%', marginTop: '5px' }}>
                        <button className="btn btn-sm btn-outline-dark" style={{ flex: 1, padding: '4px' }} onClick={() => handleEditClick('csr', item)}><FaEdit /></button>
                        <button className="btn btn-sm btn-outline-danger" style={{ flex: 1, padding: '4px' }} onClick={() => handleDeleteClick('csr', item.id)}><FaTrash /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 4: CSR Projects */}
          {activeTab === 'project' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>CSR Projects Showcase</h2>
                <button className="btn btn-primary" onClick={() => { setModalType('project'); setShowModal(true); }}>
                  <FaPlus /> Add CSR Project
                </button>
              </div>

              {collabProjects.length === 0 ? (
                <p>No CSR projects showcase items added yet.</p>
              ) : (
                <div className="items-grid" style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                  {collabProjects.map(item => {
                    let parsedHighlights = [];
                    try {
                      parsedHighlights = typeof item.highlights === 'string' ? JSON.parse(item.highlights) : (item.highlights || []);
                    } catch (e) {
                      console.error(e);
                    }

                    return (
                      <div key={item.id} className="item-card" style={{ border: '1px solid #eee', padding: '20px', borderRadius: '8px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <div style={{ width: '220px', minWidth: '180px', height: '140px', background: '#f5f5f5', borderRadius: '6px', overflow: 'hidden' }}>
                          <img src={getImageUrl(item.image)} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <h3 style={{ margin: '0' }}>{item.title}</h3>
                          <p style={{ fontSize: '0.9rem', color: '#555', margin: '0' }}>{item.description}</p>
                          <p style={{ fontSize: '0.85rem', color: '#777', fontStyle: 'italic', margin: '0' }}>{item.details}</p>
                          
                          {parsedHighlights.length > 0 && (
                            <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                              {parsedHighlights.map((hl, idx) => (
                                <div key={idx} style={{ background: '#f0f7f9', padding: '5px 10px', borderRadius: '4px' }}>
                                  <strong style={{ color: '#00526A' }}>{hl.number}</strong> <span style={{ fontSize: '0.8rem', color: '#666' }}>{hl.label}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: '10px', marginTop: '15px', maxWidth: '200px' }}>
                            <button className="btn btn-sm btn-outline-dark" style={{ flex: 1 }} onClick={() => handleEditClick('project', item)}><FaEdit /> Edit</button>
                            <button className="btn btn-sm btn-outline-danger" style={{ flex: 1 }} onClick={() => handleDeleteClick('project', item.id)}><FaTrash /> Delete</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 5: Why Partner Features */}
          {activeTab === 'feature' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Why Collaborate Features</h2>
                <button className="btn btn-primary" onClick={() => { setModalType('feature'); setShowModal(true); }}>
                  <FaPlus /> Add Feature
                </button>
              </div>

              {collabFeatures.length === 0 ? (
                <p>No features added yet.</p>
              ) : (
                <div className="items-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                  {collabFeatures.map(item => (
                    <div key={item.id} className="item-card" style={{ border: '1px solid #eee', padding: '15px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '2rem' }}>{item.icon}</span>
                        <h3 style={{ margin: '0' }}>{item.title}</h3>
                      </div>
                      <p style={{ fontSize: '0.85rem', color: '#666', flexGrow: 1 }}>{item.description}</p>
                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <button className="btn btn-sm btn-outline-dark" style={{ flex: 1 }} onClick={() => handleEditClick('feature', item)}><FaEdit /> Edit</button>
                        <button className="btn btn-sm btn-outline-danger" style={{ flex: 1 }} onClick={() => handleDeleteClick('feature', item.id)}><FaTrash /> Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* CRUD MODAL */}
      {showModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: '0', left: '0', right: '0', bottom: '0', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: '1000' }}>
          <div className="modal-content" style={{ background: '#fff', padding: '25px', borderRadius: '8px', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
            <button style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer' }} onClick={resetFormState}>
              <FaTimes />
            </button>

            <h2>{editingId ? 'Edit' : 'Add New'} {modalType.charAt(0).toUpperCase() + modalType.slice(1)}</h2>

            <form onSubmit={handleModalSubmit} style={{ marginTop: '20px' }}>
              {/* Form 1: Company Partner */}
              {modalType === 'company' && (
                <>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Partner Name</label>
                    <input type="text" className="form-control" value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} required />
                  </div>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Description</label>
                    <textarea className="form-control" rows="3" value={companyForm.description} onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Website URL</label>
                    <input type="url" className="form-control" value={companyForm.website} onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Partner Logo</label>
                    <input type="file" ref={fileInputRef} className="form-control" accept="image/*" onChange={(e) => handleFileChange(e, 'company')} />
                  </div>
                </>
              )}

              {/* Form 2: CSR Partner */}
              {modalType === 'csr' && (
                <>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Partner Name / Label</label>
                    <input type="text" className="form-control" value={csrForm.name} onChange={(e) => setCsrForm({ ...csrForm, name: e.target.value })} required />
                  </div>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Logo File</label>
                    <input type="file" ref={fileInputRef} className="form-control" accept="image/*" onChange={(e) => handleFileChange(e, 'csr')} />
                  </div>
                </>
              )}

              {/* Form 3: Why Partner Feature */}
              {modalType === 'feature' && (
                <>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Icon (Emoji/Text)</label>
                    <input type="text" className="form-control" value={featureForm.icon} onChange={(e) => setFeatureForm({ ...featureForm, icon: e.target.value })} placeholder="e.g. 🚀" required />
                  </div>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Feature Title</label>
                    <input type="text" className="form-control" value={featureForm.title} onChange={(e) => setFeatureForm({ ...featureForm, title: e.target.value })} required />
                  </div>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Description</label>
                    <textarea className="form-control" rows="3" value={featureForm.description} onChange={(e) => setFeatureForm({ ...featureForm, description: e.target.value })} required />
                  </div>
                </>
              )}

              {/* Form 4: CSR Project */}
              {modalType === 'project' && (
                <>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Project Title</label>
                    <input type="text" className="form-control" value={projectForm.title} onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })} required />
                  </div>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Summary Description</label>
                    <textarea className="form-control" rows="3" value={projectForm.description} onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })} required />
                  </div>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Detailed Info</label>
                    <textarea className="form-control" rows="3" value={projectForm.details} onChange={(e) => setProjectForm({ ...projectForm, details: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="checkbox" id="project-reverse" checked={projectForm.reverse} onChange={(e) => setProjectForm({ ...projectForm, reverse: e.target.checked })} />
                    <label htmlFor="project-reverse" style={{ margin: '0' }}>Reverse Image Layout (Alternating flow)</label>
                  </div>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label>Showcase Image</label>
                    <input type="file" ref={fileInputRef} className="form-control" accept="image/*" onChange={(e) => handleFileChange(e, 'project')} />
                  </div>

                  {/* Highlights section (Dynamic fields list) */}
                  <div className="form-group" style={{ marginBottom: '20px', border: '1px solid #eee', padding: '15px', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <label style={{ fontWeight: 'bold' }}>Key Performance Highlights</label>
                      <button type="button" className="btn btn-sm btn-outline-dark" onClick={addHighlightField}>+ Add Highlight</button>
                    </div>
                    {projectForm.highlights.map((hl, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                        <input type="text" className="form-control" style={{ flex: '1' }} placeholder="Value (e.g. 500+)" value={hl.number} onChange={(e) => handleHighlightChange(idx, 'number', e.target.value)} required />
                        <input type="text" className="form-control" style={{ flex: '2' }} placeholder="Label (e.g. Students Trained)" value={hl.label} onChange={(e) => handleHighlightChange(idx, 'label', e.target.value)} required />
                        <button type="button" className="btn btn-sm btn-outline-danger" disabled={projectForm.highlights.length <= 1} onClick={() => removeHighlightField(idx)}>&times;</button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Shared image preview inside modal */}
              {imagePreview && modalType !== 'feature' && (
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label>Current Image Preview</label>
                  <div style={{ marginTop: '5px' }}>
                    <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain', border: '1px solid #ddd' }} />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" className="btn btn-outline-dark" onClick={resetFormState}>Cancel</button>
                <button type="submit" className="btn btn-primary"><FaSave style={{ marginRight: '5px' }} /> Save Details</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCollaborations;
